import { useEffect, useId, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { FormCard } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimIncidentHref,
  buildClaimOtherMultiVehicleDriverHref,
  buildClaimOtherSingleVehicleCollisionWithHref,
  buildClaimOtherSingleVehicleDescribeHref,
  buildClaimOtherSingleVehicleDriverHref,
  buildClaimOtherWeatherDescribeHref,
  buildClaimOtherWeatherDriverHref,
  buildClaimOtherWeatherReviewHref,
  buildClaimOtherFireDescribeHref,
  buildClaimOtherFireDriverHref,
  buildClaimOtherFireReviewHref,
  buildClaimOtherMaliciousDamageDescribeHref,
  buildClaimOtherMaliciousDamageReviewHref,
  buildClaimOtherOtherLossDescribeHref,
  buildClaimOtherOtherLossReviewHref,
  buildClaimOtherTheftDescribeHref,
  buildClaimOtherTheftDriverHref,
  buildClaimOtherTheftReviewHref,
  CLAIM_FLOW_PATH,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  formatSingleVehicleCollisionRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
  parseTowingAnswerQuery,
} from '../claims/claimFlowModel'
import { parseOtherClaimSubtypeQuery } from '../claims/otherClaimSubtypeOptions'
import {
  isFireDescribeGateSatisfied,
  isFireDriverDetailsGateSatisfied,
  isMaliciousDamageDescribeGateSatisfied,
  isOtherLossDescribeGateSatisfied,
  isTheftDescribeGateSatisfied,
  isTheftDriverDetailsGateSatisfied,
  isSingleVehicleCollisionTargetGateSatisfied,
  isSingleVehicleDriverDetailsGateSatisfied,
  isWeatherDescribeGateSatisfied,
  isWeatherDriverDetailsGateSatisfied,
  persistClaimTowingAnswer,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import { useEnsureOtherBranchGstAnswered } from '../claims/useEnsureOtherBranchGstAnswered'

type TowingAnswer = 'yes' | 'no' | null

/**
 * Step after “any other type of claim”: Yes/No on whether the vehicle needs towing.
 */
export default function ClaimTowingQuestion() {
  const navigate = useNavigate()
  const location = useLocation()
  const headingId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(readClaimDraft()?.otherClaimSubtypeId ?? null)
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  useEnsureOtherBranchGstAnswered({
    claimEntryBranch: 'other',
    policyRef,
    policyTypeQuery: typeInQuery,
    otherClaimSubtype: claimSubtype,
    navPolicy: navState?.policy,
  })

  const [answer, setAnswer] = useState<TowingAnswer>(() =>
    parseTowingAnswerQuery(queryParams.get('towing'))
  )

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (claimSubtype !== null) return
    const p = new URLSearchParams()
    if (policyRef) p.set('policyRef', policyRef)
    if (typeInQuery) p.set('type', typeInQuery)
    const q = p.toString()
    const path = q ? `${CLAIM_FLOW_PATH.otherClaimSubtype}?${q}` : CLAIM_FLOW_PATH.otherClaimSubtype
    navigate(path, {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimSubtype !== 'multi-vehicle-collision') return
    const path = buildClaimOtherMultiVehicleDriverHref(policyRef, typeInQuery, claimSubtype)
    navigate(path, {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimSubtype !== 'single-vehicle') return
    const d = readClaimDraft()
    if (!isSingleVehicleDriverDetailsGateSatisfied(d)) {
      navigate(buildClaimOtherSingleVehicleDriverHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (!isSingleVehicleCollisionTargetGateSatisfied(d)) {
      navigate(buildClaimOtherSingleVehicleCollisionWithHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    navigate(buildClaimOtherSingleVehicleDescribeHref(policyRef, typeInQuery, claimSubtype), {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimSubtype !== 'weather') return
    const d = readClaimDraft()
    if (!isWeatherDriverDetailsGateSatisfied(d)) {
      navigate(buildClaimOtherWeatherDriverHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (!isWeatherDescribeGateSatisfied(d)) {
      navigate(buildClaimOtherWeatherDescribeHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    navigate(buildClaimOtherWeatherReviewHref(policyRef, typeInQuery, claimSubtype), {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimSubtype !== 'fire') return
    const d = readClaimDraft()
    if (!isFireDriverDetailsGateSatisfied(d)) {
      navigate(buildClaimOtherFireDriverHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (!isFireDescribeGateSatisfied(d)) {
      navigate(buildClaimOtherFireDescribeHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    navigate(buildClaimOtherFireReviewHref(policyRef, typeInQuery, claimSubtype), {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimSubtype !== 'malicious-damage') return
    const d = readClaimDraft()
    if (!isMaliciousDamageDescribeGateSatisfied(d)) {
      navigate(buildClaimOtherMaliciousDamageDescribeHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    navigate(buildClaimOtherMaliciousDamageReviewHref(policyRef, typeInQuery, claimSubtype), {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimSubtype !== 'other-loss') return
    const d = readClaimDraft()
    if (!isOtherLossDescribeGateSatisfied(d)) {
      navigate(buildClaimOtherOtherLossDescribeHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    navigate(buildClaimOtherOtherLossReviewHref(policyRef, typeInQuery, claimSubtype), {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    if (claimSubtype !== 'theft') return
    const d = readClaimDraft()
    if (!isTheftDriverDetailsGateSatisfied(d)) {
      navigate(buildClaimOtherTheftDriverHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (!isTheftDescribeGateSatisfied(d)) {
      navigate(buildClaimOtherTheftDescribeHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    navigate(buildClaimOtherTheftReviewHref(policyRef, typeInQuery, claimSubtype), {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  const handleBack = () => {
    if (claimSubtype === null) return
    const navOpts = navState?.policy ? { state: { policy: navState.policy } } : undefined
    navigate(buildClaimIncidentHref(policyRef, typeInQuery, 'other', claimSubtype), navOpts)
  }

  const handleTowingAnswer = (value: 'yes' | 'no') => {
    if (claimSubtype === null) return
    persistClaimTowingAnswer(policyRef, value === 'yes')
    setAnswer(value)
    const params = new URLSearchParams()
    if (policyRef) params.set('policyRef', policyRef)
    if (typeInQuery) params.set('type', typeInQuery)
    params.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
    params.set('towing', value)
    const q = params.toString()
    const nextPath = q ? `${CLAIM_FLOW_PATH.details}?${q}` : CLAIM_FLOW_PATH.details
    navigate(nextPath, {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  if (claimSubtype === null) {
    return null
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={handleBack}
        title="Towing"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={formatClaimPolicySummaryLine(
              navState?.policy ?? (policyRef ? { policyRef } : null)
            )}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(readClaimDraft())}
            towingAnswer={claimSubtype === 'single-vehicle' ? null : answer}
            windscreenGstRegistered={readClaimDraft()?.windscreenGstRegistered ?? null}
            windscreenAbn={readClaimDraft()?.windscreenAbn ?? null}
            windscreenItcEntitled={readClaimDraft()?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={readClaimDraft()?.windscreenItcPercentage ?? null}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(readClaimDraft())}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(readClaimDraft())}
            multiVehicleIncidentDescription={readClaimDraft()?.multiVehicleIncidentDescription ?? null}
            multiVehicleDamageDescription={readClaimDraft()?.multiVehicleDamageDescription ?? null}
            multiVehicleClaimDescription={readClaimDraft()?.multiVehicleClaimDescription ?? null}
            weatherClaimConditionsDescription={readClaimDraft()?.weatherClaimConditionsDescription ?? null}
            fireClaimTypeDescription={readClaimDraft()?.fireClaimTypeDescription ?? null}
            theftOrBreakInDetailDescription={readClaimDraft()?.theftOrBreakInDetailDescription ?? null}
            singleVehicleCollisionSummary={formatSingleVehicleCollisionRailSummary(readClaimDraft())}
            singleVehicleIncidentDescription={readClaimDraft()?.singleVehicleIncidentDescription ?? null}
            singleVehicleDamageDescription={readClaimDraft()?.singleVehicleDamageDescription ?? null}
          />
        }
      >
          <FormCard>
            <div className="flex w-full flex-col items-start gap-[20px]">
              <h2
                id={headingId}
                className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
              >
                Does your vehicle require towing?
              </h2>
              <fieldset
                className="m-0 w-full min-w-0 border-0 p-0"
                aria-labelledby={headingId}
              >
                <legend className="sr-only">Does your vehicle require towing? Choose yes or no.</legend>
                <TowingYesNo value={answer} onChange={handleTowingAnswer} />
              </fieldset>
            </div>
          </FormCard>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}

function TowingYesNo({
  value,
  onChange,
}: {
  value: TowingAnswer
  onChange: (v: 'yes' | 'no') => void
}) {
  return (
    <div
      className="flex w-full max-w-full overflow-clip rounded-md border border-border-primary border-solid bg-background-primary shadow-xs isolate"
      role="group"
    >
      {(
        [
          { key: 'yes' as const, label: 'Yes' },
          { key: 'no' as const, label: 'No' },
        ] as const
      ).map(({ key, label }, index) => {
        const selected = value === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={[
              'min-h-[48px] flex-1 px-[16px] py-[10px] font-body text-sm font-semibold leading-sm transition-colors duration-150',
              'focus:outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-inset',
              'active:bg-background-brand-solid active:text-text-primary-on-brand',
              index > 0 ? 'border-l border-border-primary border-solid' : '',
              selected
                ? 'bg-background-brand-solid text-text-primary-on-brand'
                : 'bg-background-primary text-text-secondary-700',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-pressed={selected}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
